import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Briefcase, Plus, Users, Calendar, FileText, Clock,
  Eye, Edit, Trash2, CheckCircle2, XCircle, AlertCircle,
  Scale, Gavel, User, Search, Filter, Download
} from 'lucide-react';
import Navbar from '../../components/layout/Navbar';
import Footer from '../../components/layout/Footer';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import api from '../../utils/api';
import { getUser } from '../../utils/auth';
import { toast } from 'sonner';
import { format } from 'date-fns';

const AdvancedCaseManagement = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const user = getUser();
  
  const [loading, setLoading] = useState(true);
  const [cases, setCases] = useState([]);
  const [lawyers, setLawyers] = useState([]);
  const [clients, setClients] = useState([]);
  const [showAddCase, setShowAddCase] = useState(false);
  const [selectedCase, setSelectedCase] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  const [newCase, setNewCase] = useState({
    title: '',
    description: '',
    client_id: '',
    case_type: '',
    department: '',
    lawyer_id: '',
    researcher_id: '',
    court_name: '',
    opposing_party: '',
    case_value: ''
  });

  const caseTypes = [
    { value: 'labor', label_ar: 'قضايا العمال', label_en: 'Labor' },
    { value: 'commercial', label_ar: 'قضايا تجارية', label_en: 'Commercial' },
    { value: 'family', label_ar: 'أحوال شخصية', label_en: 'Family' },
    { value: 'real_estate', label_ar: 'قضايا عقارية', label_en: 'Real Estate' },
    { value: 'criminal', label_ar: 'قضايا جزائية', label_en: 'Criminal' },
    { value: 'execution', label_ar: 'تنفيذ', label_en: 'Execution' },
    { value: 'administrative', label_ar: 'قضايا إدارية', label_en: 'Administrative' },
    { value: 'appeal', label_ar: 'استئناف', label_en: 'Appeal' },
    { value: 'banking', label_ar: 'قضايا مصرفية', label_en: 'Banking' }
  ];

  const departments = [
    'الدائرة العمالية',
    'الدائرة التجارية',
    'دائرة الأحوال الشخصية',
    'الدائرة العقارية',
    'الدائرة الجزائية',
    'دائرة التنفيذ',
    'دائرة القضاء الإداري',
    'دائرة الاستئناف',
    'الدائرة المصرفية والمالية'
  ];

  const caseStatuses = [
    { value: 'new', label_ar: 'جديدة', color: 'bg-blue-500' },
    { value: 'active', label_ar: 'نشطة', color: 'bg-green-500' },
    { value: 'waiting_hearing', label_ar: 'بانتظار جلسة', color: 'bg-yellow-500' },
    { value: 'in_progress', label_ar: 'قيد المعالجة', color: 'bg-purple-500' },
    { value: 'appealed', label_ar: 'مستأنفة', color: 'bg-orange-500' },
    { value: 'closed_won', label_ar: 'مغلقة - مكسوبة', color: 'bg-green-700' },
    { value: 'closed_lost', label_ar: 'مغلقة - مخسورة', color: 'bg-red-700' },
    { value: 'closed_settled', label_ar: 'مغلقة - تصالح', color: 'bg-gray-500' },
    { value: 'archived', label_ar: 'مؤرشفة', color: 'bg-gray-400' }
  ];

  useEffect(() => {
    if (user?.role !== 'admin' && user?.role !== 'lawyer') {
      toast.error(i18n.language === 'ar' ? 'غير مصرح' : 'Unauthorized');
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [casesRes, lawyersRes, clientsRes] = await Promise.all([
        api.get('/cases'),
        api.get('/users?role=lawyer'),
        api.get('/users?role=client')
      ]);
      setCases(casesRes.data);
      setLawyers(lawyersRes.data || []);
      setClients(clientsRes.data || []);
    } catch (error) {
      toast.error(i18n.language === 'ar' ? 'فشل تحميل البيانات' : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCase = async (e) => {
    e.preventDefault();
    try {
      await api.post('/cases', newCase);
      toast.success(i18n.language === 'ar' ? 'تم إضافة القضية بنجاح' : 'Case added successfully');
      setShowAddCase(false);
      setNewCase({
        title: '',
        description: '',
        client_id: '',
        case_type: '',
        department: '',
        lawyer_id: '',
        researcher_id: '',
        court_name: '',
        opposing_party: '',
        case_value: ''
      });
      fetchData();
    } catch (error) {
      toast.error(i18n.language === 'ar' ? 'فشل إضافة القضية' : 'Failed to add case');
    }
  };

  const handleUpdateCaseStatus = async (caseId, newStatus) => {
    try {
      await api.put(`/cases/${caseId}`, { status: newStatus });
      toast.success(i18n.language === 'ar' ? 'تم تحديث حالة القضية' : 'Case status updated');
      fetchData();
    } catch (error) {
      toast.error(i18n.language === 'ar' ? 'فشل التحديث' : 'Update failed');
    }
  };

  const getStatusBadge = (status) => {
    const statusInfo = caseStatuses.find(s => s.value === status);
    if (!statusInfo) return null;
    return (
      <Badge className={`${statusInfo.color} text-white`}>
        {statusInfo.label_ar}
      </Badge>
    );
  };

  const filteredCases = cases.filter(c => {
    const matchesStatus = filterStatus === 'all' || c.status === filterStatus;
    const matchesSearch = c.case_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         c.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (c.client_name && c.client_name.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesStatus && matchesSearch;
  });

  const stats = [
    {
      title: i18n.language === 'ar' ? 'إجمالي القضايا' : 'Total Cases',
      value: cases.length,
      icon: Briefcase,
      color: 'from-blue-500 to-blue-600'
    },
    {
      title: i18n.language === 'ar' ? 'قضايا نشطة' : 'Active Cases',
      value: cases.filter(c => c.status === 'active' || c.status === 'in_progress').length,
      icon: Scale,
      color: 'from-green-500 to-green-600'
    },
    {
      title: i18n.language === 'ar' ? 'قضايا مغلقة' : 'Closed Cases',
      value: cases.filter(c => c.status.startsWith('closed')).length,
      icon: CheckCircle2,
      color: 'from-purple-500 to-purple-600'
    },
    {
      title: i18n.language === 'ar' ? 'بانتظار جلسة' : 'Awaiting Hearing',
      value: cases.filter(c => c.status === 'waiting_hearing').length,
      icon: Calendar,
      color: 'from-yellow-500 to-yellow-600'
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Navbar />

      <main className="pt-24 pb-12 px-4">
        <div className="max-w-7xl mx-auto space-y-8">
          {/* Header */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h1 className="text-4xl font-bold mb-2 text-gray-900">
                {i18n.language === 'ar' ? 'إدارة القضايا المتقدمة' : 'Advanced Case Management'}
              </h1>
              <p className="text-gray-600 text-lg">
                {i18n.language === 'ar' ? 'إدارة شاملة للقضايا مع الجلسات والمذكرات والأحكام' : 'Comprehensive case management with sessions, memos and judgments'}
              </p>
            </div>
            <Dialog open={showAddCase} onOpenChange={setShowAddCase}>
              <DialogTrigger asChild>
                <Button className="btn-primary gap-2 shadow-lg">
                  <Plus className="w-5 h-5" />
                  {i18n.language === 'ar' ? 'إضافة قضية جديدة' : 'Add New Case'}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white">
                <DialogHeader>
                  <DialogTitle className="text-2xl text-gray-900">
                    {i18n.language === 'ar' ? 'إضافة قضية جديدة' : 'Add New Case'}
                  </DialogTitle>
                  <DialogDescription className="text-gray-600">
                    {i18n.language === 'ar' ? 'أدخل بيانات القضية بالكامل' : 'Enter complete case details'}
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleAddCase} className="space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <Label className="text-base font-semibold text-gray-900">{i18n.language === 'ar' ? 'عنوان القضية' : 'Case Title'}</Label>
                      <Input
                        value={newCase.title}
                        onChange={(e) => setNewCase({...newCase, title: e.target.value})}
                        className="mt-2 bg-white border-gray-300 text-gray-900"
                        required
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-base font-semibold text-gray-900">{i18n.language === 'ar' ? 'وصف القضية' : 'Case Description'}</Label>
                      <Textarea
                        value={newCase.description}
                        onChange={(e) => setNewCase({...newCase, description: e.target.value})}
                        rows={4}
                        className="mt-2 bg-white border-gray-300 text-gray-900"
                        required
                      />
                    </div>
                    <div>
                      <Label className="text-base font-semibold text-gray-900">{i18n.language === 'ar' ? 'العميل' : 'Client'}</Label>
                      <Select value={newCase.client_id} onValueChange={(value) => setNewCase({...newCase, client_id: value})}>
                        <SelectTrigger className="mt-2 bg-white border-gray-300 text-gray-900">
                          <SelectValue placeholder={i18n.language === 'ar' ? 'اختر العميل' : 'Select Client'} />
                        </SelectTrigger>
                        <SelectContent className="bg-white">
                          {clients.map(client => (
                            <SelectItem key={client.id} value={client.id}>{client.full_name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-base font-semibold text-gray-900">{i18n.language === 'ar' ? 'نوع القضية' : 'Case Type'}</Label>
                      <Select value={newCase.case_type} onValueChange={(value) => setNewCase({...newCase, case_type: value})}>
                        <SelectTrigger className="mt-2 bg-white border-gray-300 text-gray-900">
                          <SelectValue placeholder={i18n.language === 'ar' ? 'اختر النوع' : 'Select Type'} />
                        </SelectTrigger>
                        <SelectContent className="bg-white">
                          {caseTypes.map(type => (
                            <SelectItem key={type.value} value={type.value}>
                              {i18n.language === 'ar' ? type.label_ar : type.label_en}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-base font-semibold text-gray-900">{i18n.language === 'ar' ? 'القسم' : 'Department'}</Label>
                      <Select value={newCase.department} onValueChange={(value) => setNewCase({...newCase, department: value})}>
                        <SelectTrigger className="mt-2 bg-white border-gray-300 text-gray-900">
                          <SelectValue placeholder={i18n.language === 'ar' ? 'اختر القسم' : 'Select Department'} />
                        </SelectTrigger>
                        <SelectContent className="bg-white">
                          {departments.map(dept => (
                            <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-base font-semibold text-gray-900">{i18n.language === 'ar' ? 'المحامي المسؤول' : 'Assigned Lawyer'}</Label>
                      <Select value={newCase.lawyer_id} onValueChange={(value) => setNewCase({...newCase, lawyer_id: value})}>
                        <SelectTrigger className="mt-2 bg-white border-gray-300 text-gray-900">
                          <SelectValue placeholder={i18n.language === 'ar' ? 'اختر المحامي' : 'Select Lawyer'} />
                        </SelectTrigger>
                        <SelectContent className="bg-white">
                          {lawyers.map(lawyer => (
                            <SelectItem key={lawyer.id} value={lawyer.id}>{lawyer.full_name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-base font-semibold text-gray-900">{i18n.language === 'ar' ? 'المحكمة' : 'Court Name'}</Label>
                      <Input
                        value={newCase.court_name}
                        onChange={(e) => setNewCase({...newCase, court_name: e.target.value})}
                        className="mt-2 bg-white border-gray-300 text-gray-900"
                      />
                    </div>
                    <div>
                      <Label className="text-base font-semibold text-gray-900">{i18n.language === 'ar' ? 'الخصم' : 'Opposing Party'}</Label>
                      <Input
                        value={newCase.opposing_party}
                        onChange={(e) => setNewCase({...newCase, opposing_party: e.target.value})}
                        className="mt-2 bg-white border-gray-300 text-gray-900"
                      />
                    </div>
                    <div>
                      <Label className="text-base font-semibold text-gray-900">{i18n.language === 'ar' ? 'قيمة الدعوى (ريال)' : 'Case Value (SAR)'}</Label>
                      <Input
                        type="number"
                        value={newCase.case_value}
                        onChange={(e) => setNewCase({...newCase, case_value: e.target.value})}
                        className="mt-2 bg-white border-gray-300 text-gray-900"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end gap-3 pt-4 border-t">
                    <Button type="button" variant="outline" onClick={() => setShowAddCase(false)}>
                      {i18n.language === 'ar' ? 'إلغاء' : 'Cancel'}
                    </Button>
                    <Button type="submit" className="btn-primary">
                      <Plus className="w-4 h-4 ml-2" />
                      {i18n.language === 'ar' ? 'إضافة القضية' : 'Add Case'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
              <Card key={index} className="relative overflow-hidden border-0 shadow-lg hover:shadow-xl transition-all">
                <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} opacity-5`} />
                <CardContent className="pt-6 relative">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm text-gray-600 mb-2 font-medium">{stat.title}</p>
                      <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
                    </div>
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center shadow-lg`}>
                      <stat.icon className="w-6 h-6 text-white" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}</div>

          {/* Filters & Search */}
          <Card className="shadow-lg">
            <CardContent className="pt-6">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      placeholder={i18n.language === 'ar' ? 'بحث في القضايا...' : 'Search cases...'}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
                <div className="w-full md:w-64">
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{i18n.language === 'ar' ? 'جميع الحالات' : 'All Statuses'}</SelectItem>
                      {caseStatuses.map(status => (
                        <SelectItem key={status.value} value={status.value}>
                          {status.label_ar}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Cases List */}
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle className="text-2xl">{i18n.language === 'ar' ? 'قائمة القضايا' : 'Cases List'}</CardTitle>
            </CardHeader>
            <CardContent>
              {filteredCases.length === 0 ? (
                <div className="text-center py-12">
                  <Briefcase className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 text-lg">{i18n.language === 'ar' ? 'لا توجد قضايا' : 'No cases found'}</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredCases.map((caseItem) => (
                    <div
                      key={caseItem.id}
                      className="p-6 border rounded-xl hover:shadow-lg transition-all bg-white"
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-lg font-bold text-gray-900">{caseItem.case_number}</h3>
                            {getStatusBadge(caseItem.status)}
                            <Badge variant="outline" className="text-xs">
                              {caseTypes.find(t => t.value === caseItem.case_type)?.[i18n.language === 'ar' ? 'label_ar' : 'label_en']}
                            </Badge>
                          </div>
                          <h4 className="text-base font-semibold text-gray-700 mb-2">{caseItem.title}</h4>
                          <p className="text-sm text-gray-600 mb-3">{caseItem.description}</p>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div className="flex items-center gap-2 text-gray-600">
                              <User className="w-4 h-4 text-gray-400" />
                              <span>{caseItem.client_name || 'عميل'}</span>
                            </div>
                            {caseItem.lawyer_name && (
                              <div className="flex items-center gap-2 text-gray-600">
                                <Scale className="w-4 h-4 text-gray-400" />
                                <span>{caseItem.lawyer_name}</span>
                              </div>
                            )}
                            {caseItem.court_name && (
                              <div className="flex items-center gap-2 text-gray-600">
                                <Gavel className="w-4 h-4 text-gray-400" />
                                <span>{caseItem.court_name}</span>
                              </div>
                            )}
                            <div className="flex items-center gap-2 text-gray-600">
                              <Calendar className="w-4 h-4 text-gray-400" />
                              <span>{format(new Date(caseItem.created_at), 'dd/MM/yyyy')}</span>
                            </div>
                          </div>
                          {caseItem.next_session_date && (
                            <div className="mt-3 p-3 bg-yellow-50 rounded-lg flex items-center gap-2">
                              <Clock className="w-4 h-4 text-yellow-600" />
                              <span className="text-sm text-yellow-700">
                                {i18n.language === 'ar' ? 'الجلسة القادمة:' : 'Next Session:'} {format(new Date(caseItem.next_session_date), 'dd/MM/yyyy')}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col gap-2 ml-4">
                          <Button
                            size="sm"
                            onClick={() => navigate(`/admin/case-details/${caseItem.id}`)}
                            className="btn-primary"
                          >
                            <Eye className="w-4 h-4 ml-2" />
                            {i18n.language === 'ar' ? 'التفاصيل' : 'Details'}
                          </Button>
                          <Select
                            value={caseItem.status}
                            onValueChange={(value) => handleUpdateCaseStatus(caseItem.id, value)}
                          >
                            <SelectTrigger className="w-32">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {caseStatuses.map(status => (
                                <SelectItem key={status.value} value={status.value}>
                                  {status.label_ar}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-gray-500 pt-3 border-t">
                        <span>{caseItem.sessions_count || 0} {i18n.language === 'ar' ? 'جلسة' : 'sessions'}</span>
                        <span>{caseItem.documents_count || 0} {i18n.language === 'ar' ? 'مستند' : 'documents'}</span>
                        <span>{caseItem.memos_count || 0} {i18n.language === 'ar' ? 'مذكرة' : 'memos'}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AdvancedCaseManagement;
